import { CompassOutlined, EditOutlined, UpSquareOutlined } from '@ant-design/icons';
import { App, Input, Tooltip } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import TextArea from 'antd/lib/input/TextArea';
import type React from 'react';
import { useEffect, useState } from 'react';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import type { GetOrderByIdWithDriversWarehousesQuery } from '@/generated/graphql/graphql';
import { LocationSearchInputView } from '../../../components/LocationSearchInputView';
import { useLocationSearch } from '../../../hooks/useLocationSearch';

interface AddressSectionProps {
  orderData: NonNullable<GetOrderByIdWithDriversWarehousesQuery['order_by_pk']>;
  onAddressChange: (field: string, value: string | number | null) => void;
  currentStreet?: string;
  currentComments?: string;
  isDisabled?: boolean;
}

interface AddressParts {
  country?: string;
  city?: string;
  district?: string;
  lat?: string;
  lng?: string;
}

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export const AddressSection: React.FC<AddressSectionProps> = ({
  orderData,
  onAddressChange,
  currentStreet,
  currentComments,
  isDisabled = false,
}) => {
  const { message } = App.useApp();
  const [addressParts, setAddressParts] = useState<AddressParts>({});
  const [coordinatesInputValue, setCoordinatesInputValue] = useState('');
  const [enterAddressManually, setEnterAddressManually] = useState(false);
  const [isTypingCoordinates, setIsTypingCoordinates] = useState(false);

  useEffect(() => {
    if (orderData.client?.address) {
      const address = orderData.client.address;
      setAddressParts({
        city: address.city || '',
        district: address.district || address.client?.[0]?.district?.name || '',
        country: address.country || '',
        lat: address.lat?.toString() || '',
        lng: address.lng?.toString() || '',
      });
      setCoordinatesInputValue([address.lat, address.lng].filter((v) => v).join(', '));
    }
  }, [orderData]);

  const search = useLocationSearch('');
  const parseCoordinate = (value: string) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleSelect = async (suggestion: { description: string; placeId?: string }) => {
    try {
      if (!suggestion?.placeId) return;

      if (!window.google?.maps?.places) {
        message.error('Google Maps is not loaded yet. Please try again.');
        return;
      }

      const geocode = await geocodeByPlaceId(suggestion.placeId);
      const address = geocode[0].address_components;
      const geometry = geocode[0].geometry;

      const country = address.find((v: GoogleAddressComponent) => v.types[0] === 'country');
      const city = address.find(
        (v: GoogleAddressComponent) => v.types[0] === 'administrative_area_level_1'
      );
      const district = address.find((v: GoogleAddressComponent) => v.types[0] === 'locality');

      const lat = geometry?.location?.lat();
      const lng = geometry?.location?.lng();

      const newAddressParts = {
        country: country?.long_name || '',
        city: city?.long_name || '',
        district: district?.long_name || '',
        lat: lat !== undefined && lat !== null ? lat.toString() : '',
        lng: lng !== undefined && lng !== null ? lng.toString() : '',
      };

      setAddressParts((prev) => ({ ...prev, ...newAddressParts }));
      onAddressChange('country', newAddressParts.country);
      onAddressChange('city', newAddressParts.city);
      onAddressChange('district', newAddressParts.district);
      onAddressChange('lat', lat ?? null);
      onAddressChange('lng', lng ?? null);
      search.onSelect({ description: suggestion.description, placeId: suggestion.placeId });
    } catch (_error) {
      message.error('Failed to geocode address');
    }
  };

  useEffect(() => {
    if (!enterAddressManually) {
      search.onChange(
        [addressParts.district, addressParts.city, addressParts.country].filter((v) => v).join(', ')
      );
      search.setDropdownHidden(true);
    }
    // eslint-disable-next-line
  }, [addressParts, enterAddressManually, search.onChange, search.setDropdownHidden]);

  useEffect(() => {
    if (!isTypingCoordinates && (addressParts.lat || addressParts.lng)) {
      const newCoordinatesValue = [addressParts.lat, addressParts.lng]
        .filter((v) => v && v !== '')
        .join(', ');
      if (newCoordinatesValue) {
        setCoordinatesInputValue(newCoordinatesValue);
      }
    }
  }, [addressParts.lat, addressParts.lng, isTypingCoordinates]);

  const handleAddressPartChange = (part: keyof AddressParts, value: string) => {
    setAddressParts((prev) => ({ ...prev, [part]: value }));
    onAddressChange(part, value);
  };

  const handleCoordinatesChange = (value: string) => {
    setIsTypingCoordinates(true);
    setCoordinatesInputValue(value);

    if (value.trim() === '') {
      setAddressParts((prev) => ({ ...prev, lat: '', lng: '' }));
      onAddressChange('lat', null);
      onAddressChange('lng', null);
    } else {
      const parts = value.split(',').map((part) => part.trim());

      if (parts.length === 2 && parts[0] && parts[1]) {
        const [latStr, lngStr] = parts;
        const lat = parseCoordinate(latStr);
        const lng = parseCoordinate(lngStr);

        if (lat !== null && lng !== null) {
          setAddressParts((prev) => ({ ...prev, lat: latStr, lng: lngStr }));
          onAddressChange('lat', lat);
          onAddressChange('lng', lng);
        } else {
          onAddressChange('lat', null);
          onAddressChange('lng', null);
        }
      }
    }

    setTimeout(() => setIsTypingCoordinates(false), 500);
  };

  const openMapUrl = () => {
    const coordinates = [addressParts.lat, addressParts.lng].filter((v) => v).join(', ');
    const url = `https://www.google.com/maps?q=${coordinates}`;
    window.open(url, '_blank');
  };

  return (
    <>
      {enterAddressManually ? (
        <>
          <FormItem className="app-form-item" label="Country">
            <Input
              placeholder="Country"
              value={addressParts.country}
              onChange={(e) => handleAddressPartChange('country', e.target.value)}
              disabled={isDisabled}
            />
          </FormItem>
          <FormItem className="app-form-item" label="City">
            <Input
              placeholder="City"
              value={addressParts.city}
              onChange={(e) => handleAddressPartChange('city', e.target.value)}
              disabled={isDisabled}
            />
          </FormItem>
          <FormItem className="app-form-item" label="District">
            <Input
              placeholder="District"
              value={addressParts.district}
              onChange={(e) => handleAddressPartChange('district', e.target.value)}
              disabled={isDisabled}
            />
          </FormItem>
          <div style={{ textAlign: 'right' }}>
            <Tooltip title="Use autocomplete" placement="left">
              <UpSquareOutlined onClick={() => setEnterAddressManually(false)} />
            </Tooltip>
          </div>
        </>
      ) : (
        <FormItem className="app-form-item" label="District">
          <LocationSearchInputView
            options={{ types: ['(cities)'] }}
            value={search.value}
            dropdownHidden={search.dropdownHidden}
            setDropdownHidden={search.setDropdownHidden}
            onChange={search.onChange}
            onSelect={handleSelect}
            onManually={() => setEnterAddressManually(true)}
            inputProps={{
              disabled: isDisabled,
              suffix: (
                <Tooltip title="Edit manually as parts" placement="left">
                  <EditOutlined onClick={() => setEnterAddressManually(true)} />
                </Tooltip>
              ),
            }}
          />
        </FormItem>
      )}

      <FormItem className="app-form-item" label="Street, house">
        <TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          placeholder="Street, house"
          value={
            currentStreet !== undefined ? currentStreet : orderData.client?.address?.street || ''
          }
          onChange={(e) => onAddressChange('street', e.target.value)}
          disabled={isDisabled}
        />
      </FormItem>

      <FormItem className="app-form-item" label="Coordinates">
        <Input
          placeholder="Enter coordinates separated by commas..."
          value={coordinatesInputValue}
          onChange={(e) => handleCoordinatesChange(e.target.value)}
          addonAfter={<CompassOutlined onClick={openMapUrl} />}
          disabled={isDisabled}
        />
      </FormItem>

      <FormItem className="app-form-item" label="Address note">
        <TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={
            currentComments !== undefined
              ? currentComments
              : orderData.client?.address?.comments || ''
          }
          onChange={(e) => onAddressChange('comments', e.target.value)}
          disabled={isDisabled}
        />
      </FormItem>
    </>
  );
};
